import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl'

import * as breakWorksActions from "../../redux/actions/breakworks";

import Api from 'src/services/api';

import * as AgentWorkspaceService from '../../services/agentworkspace.service';

import PropTypes from "prop-types";

import { Tooltip } from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';

import BreakReasonConfirmDialog from './break-reason-confirm-dialog';
import BreakReasonTimerSimple from './break-reason-timer-simple';
import BreakReasonTimerPage from './break-reason-timer-page';

import { EndBreakButton, Separator, TimeDisplay, TimerContainer } from './styles';

import { parseIntervalToMs, formatTime, formatIntervalToReadable } from 'src/utils/breakReason';

const defaultState = {
	loading: false,
	warningThreshold: 0.8,
	openDialog: false
};

const BreakReasonTimer = ({
	timerStyleType, // 'header' ou 'page'
	simple = false,
	isFinishingBreak = false,
	onBeforeEndBreak = null,
	setState: setParentState,
	children = null,
	onClose,
	onAfterHandleFinishBreak = null,
	...props
}) => {
	const [state, setState] = useState(defaultState);
	const [elapsedTime, setElapsedTime] = useState(0);

	const { breakworks } = props;
	const breakreasonactive = breakworks?.breakreasonactive || {};

	// Converte o intervalo de tempo limite para ms
	const limitTimeMs = useMemo(() => {
		return parseIntervalToMs(breakreasonactive?.breaklimit);
	}, [breakreasonactive?.breaklimit]);

	// Determina o status do timer
	const status = useMemo(() => {
		if (limitTimeMs <= 0) return 'normal';
		if (elapsedTime >= limitTimeMs) return 'exceeded';
		if (elapsedTime >= limitTimeMs * state.warningThreshold) return 'warning';
		return 'normal';
	}, [elapsedTime, limitTimeMs, state.warningThreshold]);

	// Calcula tempo excedido
	const exceededTime = elapsedTime > limitTimeMs ? elapsedTime - limitTimeMs : 0;

	const tooltipText = status === 'exceeded'
		? props.intl.formatMessage({ id: "break.reason.time.exceeded.info" }, { exceededTime: formatIntervalToReadable(formatTime(exceededTime)) })
		: status === 'warning'
			? props.intl.formatMessage({ id: "break.reason.warning" })
			: props.intl.formatMessage({ id: "break.reason.hold" }, { reasonName: breakreasonactive?.reasonbreak?.name  ? `: '${breakreasonactive?.reasonbreak?.name}'` : '' });

	// Função para finalizar a pausa e atualizar o status do agente para "Disponível" caso não haja outra pausa persistente
	const finishedBreak = useCallback(async (data) => {
		try {
			setParentState({ loading: true });

			let payload = new FormData();

			payload.append("id", data.id);

			let config = {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			};

			const result = await Api.put("/break-work/finish", payload, config);

			if (!result.data.success) {
				setParentState({
					loading: false,
					openNotification: true,
					notificationVariant: "error",
					notificationMessage:
						result.data && result.data.errors
							? result.data.errors[0]
							: props.intl.formatMessage({ id: "process.error" }),
				});
			}

			const response = result.data.data;

			const persistentActiveBreakwork = response?.persistentactivebreakwork;

			if (!!persistentActiveBreakwork && !!persistentActiveBreakwork.id) {
				props.addBreakReasonActive(persistentActiveBreakwork); // Atualiza o motivo de pausa ativo no Redux caso haja outro motivo de pausa persistente

				setState(prevState => ({ ...prevState, loading: false }));

				setParentState({
					loading: false,
					openNotification: true,
					notificationVariant: "info",
					notificationMessage: props.intl.formatMessage({ id: "break.reason.persistent.active" })
				});
			} else {
				props.removeBreakReasonActive(); // Reseta o motivo de pausa ativo no Redux

				await AgentWorkspaceService.updateStatus(1); // Atualiza o status do agente para "Disponível"

				setState(prevState => ({ ...prevState, loading: false }));

				setParentState({
					loading: false,
					openNotification: true,
					notificationVariant: "success",
					notificationMessage: props.intl.formatMessage({ id: "break.reason.ends.successfully" })
				});
			}

			{ !!onClose && onClose() }

			{ !!onAfterHandleFinishBreak && await onAfterHandleFinishBreak() }

		} catch (err) {
			setParentState({
				loading: false,
				openNotification: true,
				notificationVariant: "error",
				notificationMessage:
					err.response && err.response.data && err.response.data.errors
						? err.response.data.errors[0]
						: props.intl.formatMessage({ id: "process.error" }),
			});
		} finally {
			setParentState({ loading: false });
		}
	}, [props.removeBreakReasonActive]);

	function handleOpenEndBreakDialog() {
		if (onBeforeEndBreak) {
			onBeforeEndBreak();
		}

		handleOpenOrCloseDialog();
	}

	function handleOpenOrCloseDialog() {
		setState(prevState => ({ ...prevState, openDialog: !prevState.openDialog }));
	}

	//Atualiza o tempo decorrido a cada segundo
	useEffect(() => {
		if (!!!breakreasonactive?.startdate) return;

		const updateElapsed = () => {
			const now = new Date();
			const start = new Date(breakreasonactive.startdate);
			const elapsed = now.getTime() - start.getTime();
			setElapsedTime(Math.max(0, elapsed));
		};

		updateElapsed();
		const interval = setInterval(updateElapsed, 1000);
		return () => clearInterval(interval);
	}, [breakreasonactive?.startdate]);

	// Abre o dialog automaticamente quando isFinishingBreak for true
	useEffect(() => {
		if (isFinishingBreak && breakreasonactive?.id) {
			setState(prevState => ({ ...prevState, openDialog: true }));
		}
	}, [isFinishingBreak, breakreasonactive?.id]);

	// Busca o break reason ativo via Redux (chamada única centralizada)
	useEffect(() => {
		props.fetchActiveBreakReason();
	}, []);

	// Renderizariza o children caso não haja motivo de pausa ativo
	if (!!!breakreasonactive?.id) {
		return children ? <>{children}</> : null;
	}

	function renderSimple() {
		if (simple) {
			return (
				<BreakReasonTimerSimple
					status={status}
					elapsedTime={elapsedTime}
					breaklimit={formatIntervalToReadable(breakreasonactive?.breaklimit)}
					handleOpenEndBreakDialog={handleOpenEndBreakDialog}
					intl={props.intl}
				/>
			)
		}

		return (
			<TimerContainer status={status}>
				<Tooltip title={props.intl.formatMessage({ id: "break.reason.time.end" })} arrow>
					<EndBreakButton
						status={status}
						onClick={handleOpenEndBreakDialog}
						size="small"
						className="break-reason-end-button"
					>
						<PauseCircleOutlineIcon />
					</EndBreakButton>
				</Tooltip>

				<Tooltip title={tooltipText} arrow>
					<TimeDisplay status={status}>
						{formatTime(elapsedTime)}
						<Separator status={status}>/</Separator>
						<span style={{ opacity: 0.7 }}>{formatIntervalToReadable(breakreasonactive?.breaklimit)}</span>
					</TimeDisplay>
				</Tooltip>
			</TimerContainer>
		)
	}

	return (
		<React.Fragment>

			{!isFinishingBreak && timerStyleType === 'page' && (
				<BreakReasonTimerPage
				status={status}
				breakreasonactive={breakreasonactive}
				elapsedTime={elapsedTime}
				exceededTime={formatIntervalToReadable(formatTime(exceededTime))}
				breaklimit={formatIntervalToReadable(breakreasonactive?.breaklimit)}
				handleOpenEndBreakDialog={handleOpenEndBreakDialog}
					intl={props.intl}
					backgroundColorPage={props?.backgroundColorPage}
				/>
			)}
				
			{!isFinishingBreak && timerStyleType !== 'page' && (renderSimple())}

			<BreakReasonConfirmDialog
				open={state.openDialog}
				handleOpenOrClose={() => {
					handleOpenOrCloseDialog();
					
					{!!onClose && onClose()}
				}}
				activeBreakReasonData={breakreasonactive}
				onSave={finishedBreak}
			/>
		</React.Fragment>
	);
}

BreakReasonTimer.propTypes = {
	timerStyleType: PropTypes.oneOf(['header', 'page']),
	simple: PropTypes.bool,
	onBeforeEndBreak: PropTypes.func,
	setState: PropTypes.func.isRequired,
	children: PropTypes.node,
}

const mapStateToProps = (state) => ({
	userSession: state.userSession,
	breakworks: state.breakworks,
});

const mapDispatchToProps = dispatch =>
	bindActionCreators(breakWorksActions, dispatch)

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(BreakReasonTimer));